import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import { decryptApiKeyValue } from 'Core/data/Crypto';
import { ExceptionManager } from 'Core/logManager';

const INIT_STATE = {
    items: [],
    cipherKey: null,
    loading: false,
    error: null,
};

const route = "accounts/.components/tabApiKeys/redux/slice";

const QUERY_GET_CIPHER_KEY = `
    query getAccountApiKeyCipherKey {
        result: getAccountApiKeyCipherKey
    }
`;

const getCipherKey = async (getState, dispatch) => {
    let cipherKey = getState()?.tabApiKeysRedux?.cipherKey;
    if (cipherKey) return cipherKey;

    const cipherKeyResponse = await clientQuery(QUERY_GET_CIPHER_KEY, {}, endpoints.GRAPHQL_GENERAL);
    cipherKey = cipherKeyResponse?.result;
    dispatch(setCipherKey(cipherKey));
    return cipherKey;
};

// List load: only reads name/apiKeyLast4/description/rateLimit — none of this touches the
// real secret value, so it stays fast even for accounts with hundreds of api keys.
export const getAccountApiKeys = createAsyncThunk("accounts/tabApiKeys/getAccountApiKeys", async ({ accountId }, { rejectWithValue }) => {
    try {
        const QUERY = `
            query getAccountApiKeys($accountId: Int!) {
                result: getAccountApiKeys(accountId: $accountId) {
                    accountId
                    apiKeyId
                    apiKeyLast4
                    apiKeyLabel
                    createdOn
                    rateLimit {
                        usagePlanId
                        usagePlanName
                        burstLimit
                        rateLimit
                        quotaLimit
                        quotaOffset
                        quotaPeriod
                    }
                }
            }
        `;

        const response = await clientQuery(QUERY, { accountId }, endpoints.GRAPHQL_GENERAL);
        return response?.result || [];
    } catch (exc) {
        ExceptionManager(exc, route, 'getAccountApiKeys');
        return rejectWithValue(exc);
    }
});

// Only called on demand (e.g. clicking the copy icon) — one AWS lookup for a single key.
export const getApiKeyValue = createAsyncThunk("accounts/tabApiKeys/getApiKeyValue", async ({ apiKeyId }, { getState, dispatch, rejectWithValue }) => {
    try {
        const cipherKey = await getCipherKey(getState, dispatch);

        const QUERY = `
            query getApiKeyValue($apiKeyId: String!) {
                result: getApiKeyValue(apiKeyId: $apiKeyId)
            }
        `;

        const response = await clientQuery(QUERY, { apiKeyId }, endpoints.GRAPHQL_GENERAL);
        return decryptApiKeyValue(response?.result, cipherKey);
    } catch (exc) {
        ExceptionManager(exc, route, 'getApiKeyValue');
        return rejectWithValue(exc);
    }
});

export const tabApiKeysRedux = createSlice({
    name: 'accounts/tabApiKeys',
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE }
        },
        setCipherKey(state, action) {
            state.cipherKey = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getAccountApiKeys.pending, (state) => {
            state.loading = true;
            state.error = null;
        });

        builder.addCase(getAccountApiKeys.fulfilled, (state, action) => {
            state.loading = false;
            state.items = action.payload || [];
        });

        builder.addCase(getAccountApiKeys.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    }
});

const { actions, reducer } = tabApiKeysRedux;

export const { reset, setCipherKey } = actions;

export default reducer;
